import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CropPicker } from '../ui/CropPicker';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  CalendarDays, Sprout, Snowflake, Sun, Droplets, 
  Plus, Check, Trash2, Loader2,
  Leaf, AlertTriangle, Search, Filter, BookOpen, Thermometer,
  Heart, Users, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  planned: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  planted: 'bg-primary/10 text-primary border-primary/30',
  growing: 'bg-green-500/10 text-green-600 border-green-500/30',
  harvested: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30'
};

const waterIcons = {
  low: <Droplets className="w-4 h-4 text-blue-300" />,
  medium: <Droplets className="w-4 h-4 text-blue-500" />,
  high: <Droplets className="w-4 h-4 text-blue-700" />
};

const frostBadge = {
  tender: { label: 'Frost Tender', color: 'bg-red-500/10 text-red-600' },
  'semi-hardy': { label: 'Semi-Hardy', color: 'bg-yellow-500/10 text-yellow-600' },
  hardy: { label: 'Frost Hardy', color: 'bg-green-500/10 text-green-600' }
};

const SeasonPlanner = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [frostDates, setFrostDates] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [plans, setPlans] = useState([]);
  const [crops, setCrops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalCrops, setTotalCrops] = useState(0);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBrowseDialogOpen, setIsBrowseDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('recommendations');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [browseCrops, setBrowseCrops] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [guilds, setGuilds] = useState([]);
  const [companionSuggestions, setCompanionSuggestions] = useState(null);
  const [showCompanions, setShowCompanions] = useState(false);
  const [formData, setFormData] = useState({
    crop_id: '',
    planned_date: '',
    quantity: '',
    notes: ''
  });

  // Local storage cache helpers
  const getCachedData = (key) => {
    try {
      const cached = localStorage.getItem(`planner_${key}`);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Cache valid for 1 hour
        if (Date.now() - timestamp < 3600000) return data;
      }
    } catch (error) {
      console.warn('Cache read error:', error.message);
    }
    return null;
  };

  const setCachedData = (key, data) => {
    try {
      localStorage.setItem(`planner_${key}`, JSON.stringify({ data, timestamp: Date.now() }));
    } catch (error) {
      console.warn('Cache write error:', error.message);
    }
  };

  const fetchData = async () => {
    // Load cached data immediately for instant UI
    const cachedCrops = getCachedData('crops');
    const cachedCategories = getCachedData('categories');
    const cachedGuilds = getCachedData('guilds');
    
    if (cachedCrops) setCrops(cachedCrops);
    if (cachedCategories) {
      setCategories(cachedCategories.categories || []);
      setTotalCrops(cachedCategories.total_crops || 0);
    }
    if (cachedGuilds) setGuilds(cachedGuilds);
    
    // If we have cached static data, show UI immediately
    if (cachedCrops && cachedCategories) {
      setLoading(false);
    }

    try {
      // Fetch user-specific data first (fast)
      const [frostRes, plansRes, calendarRes] = await Promise.all([
        axios.get(`${API_URL}/api/planner/frost-dates`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/planner/plans`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/planner/calendar`, { headers: getAuthHeader() })
      ]);
      
      setFrostDates(frostRes.data.frost_dates);
      setPlans(plansRes.data.plans || []);
      setCalendarEvents(calendarRes.data.events || []);
      
      // Fetch static data in background (crops, categories, guilds)
      const [recsRes, cropsRes, categoriesRes, guildsRes] = await Promise.all([
        axios.get(`${API_URL}/api/planner/recommendations`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/planner/crops`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/planner/crops/categories`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/planner/guilds`, { headers: getAuthHeader() })
      ]);
      
      setRecommendations(recsRes.data.recommendations || []);
      
      // Update and cache static data
      const cropsData = cropsRes.data.crops || [];
      setCrops(cropsData);
      setCachedData('crops', cropsData);
      
      const catData = { categories: categoriesRes.data.categories || [], total_crops: categoriesRes.data.total_crops || 0 };
      setCategories(catData.categories);
      setTotalCrops(catData.total_crops);
      setCachedData('categories', catData);
      
      const guildsData = guildsRes.data.guilds || [];
      setGuilds(guildsData);
      setCachedData('guilds', guildsData);
      
    } catch (error) {
      console.error('Failed to fetch planner data:', error);
      toast.error('Failed to load planner data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch companion suggestions when plans change
  const fetchCompanionSuggestions = async () => {
    if (plans.length === 0) {
      setCompanionSuggestions(null);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/planner/companions`, { headers: getAuthHeader() });
      setCompanionSuggestions(res.data);
    } catch (error) {
      console.error('Failed to fetch companion suggestions:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (plans.length > 0) {
      fetchCompanionSuggestions();
    }
  }, [plans]);

  const handleAddPlan = async () => {
    if (!formData.crop_id || !formData.planned_date) {
      toast.error('Please select a crop and planting date');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/planner/plans`,
        formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
      );
      toast.success('Planting plan added!');
      setIsDialogOpen(false);
      setFormData({ crop_id: '', planned_date: '', quantity: '', notes: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to add plan:', error);
      toast.error('Failed to add plan');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (planId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/planner/plans/${planId}`,
        { status: newStatus },
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
      );
      toast.success(`Updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Delete this planting plan?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/planner/plans/${planId}`, {
        headers: getAuthHeader()
      });
      toast.success('Plan deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const quickAddCrop = (cropId) => {
    const crop = crops.find(c => c.id === cropId) || browseCrops.find(c => c.id === cropId);
    const rec = recommendations.find(r => r.crop_id === cropId);
    if (crop) {
      setFormData({
        crop_id: cropId,
        planned_date: rec?.plant_window_start || format(new Date(), 'yyyy-MM-dd'),
        quantity: '',
        notes: ''
      });
      setIsBrowseDialogOpen(false);
      setIsDialogOpen(true);
    }
  };

  const fetchBrowseCrops = async (category = null, search = null) => {
    setBrowseLoading(true);
    try {
      let url = `${API_URL}/api/planner/crops?limit=100`;
      if (category && category !== 'all') url += `&category=${category}`;
      if (search && search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
      
      const headers = getAuthHeader();
      const res = await axios.get(url, { headers });
      setBrowseCrops(res.data.crops || []);
    } catch (error) {
      console.error('Failed to fetch crops:', error);
      toast.error('Failed to load crops');
      setBrowseCrops([]);
    } finally {
      setBrowseLoading(false);
    }
  };

  // Fetch crops when browse dialog opens or filters change
  useEffect(() => {
    if (isBrowseDialogOpen) {
      const category = selectedCategory === 'all' ? null : selectedCategory;
      const search = searchQuery.trim() || null;
      fetchBrowseCrops(category, search);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrowseDialogOpen, selectedCategory, searchQuery]);

  const getDayEvents = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarEvents.filter(e => e.date === dateStr);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="planner-loading">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Season Planner</h1>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in w-full overflow-hidden" data-testid="season-planner">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            Season Planner
          </h1>
          <p className="text-muted-foreground mt-1">
            Plan your planting season • <span className="text-primary font-medium">{totalCrops} crops</span> in database
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setIsBrowseDialogOpen(true)}
            data-testid="browse-crops-btn"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Browse Crops
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-plan-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Crop
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" aria-describedby="add-plan-description">
              <DialogHeader>
                <DialogTitle className="font-heading">Add Planting Plan</DialogTitle>
              </DialogHeader>
              <p id="add-plan-description" className="sr-only">Add a new crop to your planting schedule</p>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Crop</Label>
                  <CropPicker
                    crops={crops}
                    value={formData.crop_id}
                    onChange={(v) => setFormData({ ...formData, crop_id: v })}
                    placeholder="Search 700+ crops"
                    dataTestId="crop-select"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="planned_date">Planting Date</Label>
                  <Input
                    id="planned_date"
                    type="date"
                    value={formData.planned_date}
                    onChange={(e) => setFormData({ ...formData, planned_date: e.target.value })}
                    className="input-farm"
                    data-testid="date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity (optional)</Label>
                  <Input
                    id="quantity"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    placeholder="e.g., 24 plants, 2 rows"
                    className="input-farm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="e.g., South garden bed"
                  className="input-farm"
                />
              </div>
              <Button 
                onClick={handleAddPlan} 
                className="btn-primary w-full" 
                disabled={saving}
                data-testid="save-plan-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add to Plan'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Browse Crops Dialog */}
      <Dialog open={isBrowseDialogOpen} onOpenChange={setIsBrowseDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" aria-describedby="browse-crops-description">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Crop Database ({totalCrops} varieties)
            </DialogTitle>
          </DialogHeader>
          <p id="browse-crops-description" className="sr-only">Browse and search through all available crops</p>
          
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-3 py-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search crops..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="crop-search-input"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name} ({cat.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Crops List */}
          <div className="flex-1 overflow-y-auto min-h-0 pr-2 -mr-2">
            {browseLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : browseCrops.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No crops found matching your search</p>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {browseCrops.map((crop) => (
                  <div
                    key={crop.id}
                    className="p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors"
                    data-testid={`browse-crop-${crop.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground truncate">{crop.name}</h4>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          <Badge variant="outline" className="text-xs">
                            {crop.category?.replace('_', ' ')}
                          </Badge>
                          {crop.frost_tolerance && frostBadge[crop.frost_tolerance] && (
                            <Badge className={`text-xs ${frostBadge[crop.frost_tolerance].color}`}>
                              <Thermometer className="w-3 h-3 mr-1" />
                              {frostBadge[crop.frost_tolerance].label}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {crop.days_to_maturity} days
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{crop.tips}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => quickAddCrop(crop.id)}
                        className="text-primary shrink-0"
                        data-testid={`add-browse-${crop.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="pt-3 border-t text-xs text-muted-foreground">
            Showing {browseCrops.length} crops • Data sourced from Baker Creek Heirloom Seeds
          </div>
        </DialogContent>
      </Dialog>

      {/* Frost Dates Card */}
      {frostDates && (
        <Card className="card-farm bg-gradient-to-r from-blue-50 to-orange-50 dark:from-blue-950/20 dark:to-orange-950/20 max-w-full" data-testid="frost-dates-card">
          <CardContent className="py-4 px-3 sm:py-6 sm:px-6 overflow-hidden">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Snowflake className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Last Frost</p>
                  <p className="font-heading font-semibold text-foreground text-sm sm:text-base truncate">{frostDates.last_frost_display}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                  <Snowflake className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">First Frost</p>
                  <p className="font-heading font-semibold text-foreground text-sm sm:text-base truncate">{frostDates.first_frost_display}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Growing Season</p>
                  <p className="font-heading font-semibold text-foreground text-sm sm:text-base">{frostDates.growing_season_days} days</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Based on</p>
                  <p className="font-heading font-semibold text-foreground text-sm sm:text-base">{frostDates.latitude?.toFixed(1)}° Lat</p>
                </div>
              </div>
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-3 sm:mt-4">
              Frost dates are estimates. Check your local extension office for precise dates.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card className="card-farm max-w-full overflow-hidden" data-testid="recommendations-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Sprout className="w-5 h-5 text-primary" />
              What to Plant Now
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-hidden">
            <div className="grid gap-3 sm:grid-cols-2">
              {recommendations.slice(0, 6).map((rec, index) => (
                <div 
                  key={rec.crop_id}
                  className={`p-3 sm:p-4 rounded-lg border animate-fade-in stagger-${index + 1} overflow-hidden ${
                    rec.status === 'plant_now' 
                      ? 'bg-primary/5 border-primary/30' 
                      : 'bg-muted/50 border-border'
                  }`}
                  data-testid={`rec-${rec.crop_id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <h3 className="font-medium text-foreground text-sm truncate">{rec.crop_name}</h3>
                        {waterIcons[rec.water_needs]}
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {rec.status === 'plant_now' ? (
                          <span className="text-primary font-medium">
                            Plant now! {rec.days_until_action}d left
                          </span>
                        ) : (
                          <span>Plant in {rec.days_until_action}d</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {rec.days_to_maturity}d to harvest
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {!rec.already_planned ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => quickAddCrop(rec.crop_id)}
                          className="text-primary h-8 w-8 p-0"
                          data-testid={`add-${rec.crop_id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">
                          <Check className="w-3 h-3 mr-0.5" />
                          <span className="hidden sm:inline">Planned</span>
                          <span className="sm:hidden">✓</span>
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Companion Planting Guilds */}
      <Card className="card-farm max-w-full overflow-hidden" data-testid="companion-planting-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2 min-w-0">
              <Heart className="w-5 h-5 text-pink-500 flex-shrink-0" />
              <span className="truncate">Companion Planting</span>
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCompanions(!showCompanions)}
              className="text-muted-foreground flex-shrink-0"
            >
              {showCompanions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Plant combinations that help each other thrive
          </p>
        </CardHeader>
        {showCompanions && (
          <CardContent className="overflow-hidden">
            {/* Companion Suggestions based on plans */}
            {companionSuggestions && companionSuggestions.recommended_crops?.length > 0 && (
              <div className="mb-6 p-3 sm:p-4 rounded-lg bg-green-500/5 border border-green-500/20 overflow-hidden">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <h4 className="font-medium text-green-700 dark:text-green-400 text-sm">
                    Suggested Companions
                  </h4>
                </div>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {companionSuggestions.recommended_crops.slice(0, 6).map((crop, idx) => (
                    <Badge 
                      key={idx}
                      variant="outline" 
                      className="bg-white dark:bg-card cursor-pointer hover:border-primary text-xs"
                      onClick={() => quickAddCrop(crop.id)}
                    >
                      <Plus className="w-3 h-3 mr-0.5" />
                      {crop.name}
                    </Badge>
                  ))}
                </div>
                {companionSuggestions.warnings?.length > 0 && (
                  <div className="mt-3 text-xs text-yellow-600 dark:text-yellow-400">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    {companionSuggestions.warnings[0]}
                  </div>
                )}
              </div>
            )}

            {/* Popular Guilds */}
            <h4 className="font-medium text-foreground mb-3 flex items-center gap-2 text-sm">
              <Users className="w-4 h-4" />
              Popular Planting Guilds
            </h4>
            <div className="grid gap-3 sm:grid-cols-2">
              {guilds.slice(0, 6).map((guild, index) => (
                <div 
                  key={guild.name}
                  className="p-3 sm:p-4 rounded-lg border bg-card hover:border-primary/50 transition-colors overflow-hidden"
                >
                  <h5 className="font-medium text-foreground mb-1 text-sm truncate">{guild.name}</h5>
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{guild.description}</p>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {guild.crops.map((crop) => (
                      <Badge key={crop} variant="outline" className="text-[10px] sm:text-xs capitalize">
                        {crop}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-primary">{guild.benefit}</p>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* My Planting Plans */}
      <Card className="card-farm" data-testid="plans-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            My Planting Plans
          </CardTitle>
        </CardHeader>
        <CardContent>
          {plans.length > 0 ? (
            <div className="space-y-3">
              {plans.map((plan) => (
                <div 
                  key={plan.id}
                  className="p-3 sm:p-4 rounded-lg bg-muted/50 overflow-hidden"
                  data-testid={`plan-${plan.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Leaf className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-foreground text-sm truncate">{plan.crop_name}</h3>
                        <Badge className={`text-[10px] ${statusColors[plan.status] || statusColors.planned}`}>
                          {plan.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Plant: {format(new Date(plan.planned_date), 'MMM d')}
                        {plan.expected_harvest && ` → Harvest: ${format(new Date(plan.expected_harvest), 'MMM d')}`}
                      </p>
                      {plan.quantity && (
                        <p className="text-xs text-muted-foreground">{plan.quantity}</p>
                      )}
                      <div className="flex items-center gap-1 mt-2">
                        {plan.status === 'planned' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateStatus(plan.id, 'planted')}
                            className="text-primary h-7 text-xs px-2"
                          >
                            <Sprout className="w-3 h-3 mr-1" />
                            Planted
                          </Button>
                        )}
                        {plan.status === 'planted' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateStatus(plan.id, 'growing')}
                            className="text-green-600 h-7 text-xs px-2"
                          >
                            <Sun className="w-3 h-3 mr-1" />
                            Growing
                          </Button>
                        )}
                        {plan.status === 'growing' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateStatus(plan.id, 'harvested')}
                            className="text-yellow-600 h-7 text-xs px-2"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Harvested
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePlan(plan.id)}
                          className="text-destructive h-7 w-7 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No planting plans yet</p>
              <Button onClick={() => setIsDialogOpen(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Crop
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calendar View */}
      <Card className="card-farm max-w-full overflow-hidden" data-testid="calendar-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Planting Calendar</CardTitle>
        </CardHeader>
        <CardContent className="overflow-hidden">
          <div className="flex flex-col md:flex-row gap-4 md:gap-6">
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-md border mx-auto"
                modifiers={{
                  planting: calendarEvents.filter(e => e.type === 'planting').map(e => new Date(e.date)),
                  harvest: calendarEvents.filter(e => e.type === 'harvest').map(e => new Date(e.date)),
                  frost: calendarEvents.filter(e => e.type === 'frost').map(e => new Date(e.date))
                }}
                modifiersStyles={{
                  planting: { backgroundColor: 'hsl(var(--primary) / 0.2)' },
                  harvest: { backgroundColor: 'hsl(45 93% 47% / 0.2)' },
                  frost: { backgroundColor: 'hsl(210 100% 50% / 0.2)' }
                }}
              />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-3 text-center md:text-left">
                {format(selectedDate, 'MMMM d, yyyy')}
              </h4>
              {getDayEvents(selectedDate).length > 0 ? (
                <div className="space-y-2">
                  {getDayEvents(selectedDate).map((event) => (
                    <div 
                      key={event.id}
                      className={`p-3 rounded-lg ${
                        event.type === 'planting' ? 'bg-primary/10' :
                        event.type === 'harvest' ? 'bg-yellow-500/10' :
                        'bg-blue-500/10'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {event.type === 'planting' && <Sprout className="w-4 h-4 text-primary" />}
                        {event.type === 'harvest' && <Check className="w-4 h-4 text-yellow-600" />}
                        {event.type === 'frost' && <Snowflake className="w-4 h-4 text-blue-500" />}
                        <span className="text-sm text-foreground">{event.title}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm text-center md:text-left">No events on this day</p>
              )}
              <div className="mt-4 pt-4 border-t border-border/60">
                <p className="text-xs text-muted-foreground mb-2 text-center md:text-left">Legend:</p>
                <div className="flex flex-wrap gap-3 text-xs justify-center md:justify-start">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-primary/20" /> Planting
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-yellow-500/20" /> Harvest
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-blue-500/20" /> Frost
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SeasonPlanner;
